import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { BVNService, BVNdetails } from '@app/services/bvn.service';
import { formatDate } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService, NzModalRef } from 'ng-zorro-antd/modal'; // Added NzModalRef
import { Router } from '@angular/router';
 
@Component({
  selector: 'app-bvn-intro',
  templateUrl: './bvn-intro.component.html',
  styleUrls: ['./bvn-intro.component.scss']
})
export class BvnIntroComponent implements OnInit, AfterViewInit {
 
  @ViewChild('termsModal', { static: false }) termsModal: TemplateRef<any>;
  @ViewChild('successModal', { static: false }) successModal: TemplateRef<any>; // Added success modal reference
  
  employeeForm: FormGroup;
  editDetailsForm: FormGroup;
  employeeInfo: any = null;
  displayInfo: boolean = false;
  error: string | null = null;
  policyVerified = false;
  fullUserDetails: BVNdetails | null = null;
  updating: boolean = false;
  hasBVN: boolean = false; // Flag to check if BVN exists
  isChecked: boolean = false; // For terms acceptance checkbox
  termsAccepted: boolean = false; // Track if terms have been accepted
  
  // Modal references for better control
  private successModalRef: NzModalRef | null = null;
 
  constructor(
    private fb: FormBuilder,
    public bVNService: BVNService,
    private cdr: ChangeDetectorRef,
    private message: NzMessageService,
    private router: Router,
    private modalService: NzModalService
  ) {
    this.employeeForm = this.fb.group({
      code: ['', Validators.required],
      dateOfBirth: [null]
    });
 
    // Form for editing all user details
    this.editDetailsForm = this.fb.group({
      policyNumber: ['', Validators.required],
      names: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telephone: ['', Validators.required],
      physicalAddress: ['', Validators.required],
      bvn: ['', [
        Validators.required,
        Validators.pattern('^[0-9]{11}$'), // Ensures exactly 11 digits
        this.numbersOnlyValidator()
      ]]
    });
  }
 
  // Custom validator to ensure only numbers
  numbersOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const value = control.value;
      if (!value) {
        return null; // Don't validate empty values
      }
      const valid = /^\d+$/.test(value);
      return valid ? null : {'numbersOnly': {value: control.value}};
    };
  }
  
  // Restrict input to only numbers
  onlyNumberKey(event: KeyboardEvent): boolean {
    // Allow only digits (0-9)
    const isNumeric = (event.charCode >= 48 && event.charCode <= 57);
    // Allow control keys (backspace, delete, arrows, etc.)
    const isControl = (event.charCode === 0);
    return isNumeric || isControl;
  }
 
  ngOnInit() {}
  
  ngAfterViewInit() {
    // Check if terms were previously accepted (in this session)
    const accepted = sessionStorage.getItem('termsAccepted');
    if (!accepted) {
      setTimeout(() => {
        this.openTermsModal();
      }, 500); // Small delay to ensure modal template is ready
    } else {
      this.termsAccepted = true;
    }
  }
  
  // Open the terms and conditions modal
  openTermsModal() {
    this.modalService.create({
      nzContent: this.termsModal,
      nzFooter: null,
      nzWidth: '800px',
      nzMaskClosable: false,
      nzClosable: false,
      nzKeyboard: false
    });
  }
  
  // Open the success modal
   openSuccessModal() {
    this.successModalRef = this.modalService.create({
      nzContent: this.successModal,
      nzFooter: null,
      nzWidth: '500px',
      nzMaskClosable: false, 
      nzClosable: false, 
      nzKeyboard: false, 
      nzClassName: 'success-modal' 
    });
  }
  
  // Close success modal
 closeSuccessModal() {
    if (this.successModalRef) {
      this.successModalRef.close();
      this.successModalRef = null;
    }
    // Route to home page after closing modal
    this.router.navigate(['/']); 
  }
  
  // Handle agreement to terms
  agree() {
    if (this.isChecked) {
      this.termsAccepted = true;
      sessionStorage.setItem('termsAccepted', 'true');
      this.modalService.closeAll();
    }
  }
 
  onSubmit() {
    if (!this.employeeForm) {
      return;
    }
    
    // Check if terms have been accepted
    if (!this.termsAccepted) {
      this.openTermsModal();
      return;
    }
 
    // If policy is already verified, check DOB next
    if (this.policyVerified) {
      this.verifyDateOfBirth();
    } else {
      this.verifyPolicyNumber();
    }
  }
 
  verifyPolicyNumber() {
    const codeControl = this.employeeForm.get('code');
    if (!codeControl || !codeControl.value) {
      console.warn('Code is empty');
      return;
    }
 
    const code = codeControl.value;
    
   
    this.bVNService.GetPolicyNumber(code).subscribe(
      bVNdetails => {
        
       
        if (bVNdetails) {
          // Get the full details for date verification
          this.getPolicyHolderFullDetails(code);
         
          // Store the basic info for display
          this.employeeInfo = bVNdetails;
         
          // Mark policy as verified and enable DOB field
          this.policyVerified = true;
         
          // Add validation to dateOfBirth now that it's required
          const dobControl = this.employeeForm.get('dateOfBirth');
          if (dobControl) {
            dobControl.setValidators(Validators.required);
            dobControl.updateValueAndValidity();
          }
         
         
          this.error = null;
        } else {
          this.error = 'Invalid policy number. Please try again.';
          this.employeeInfo = null;
          this.fullUserDetails = null;
          this.policyVerified = false;
        }
       
        this.cdr.detectChanges();
      },
      error => {
        this.error = 'Failed to fetch details. Please try again later.';
        this.policyVerified = false;
        this.cdr.detectChanges();
      }
    );
  }
 
  getPolicyHolderFullDetails(code: string) {
    this.bVNService.GetPolicyHolderDetails(code).subscribe(
      response => {
        
        if (response && response.result && response.result.length > 0) {
          this.fullUserDetails = response.result[0];
          
        }
      },
      error => {
        console.error('Failed to get full details:', error);
        this.error = 'Failed to get full details.';
      }
    );
  }
 
  verifyDateOfBirth() {
    const dobControl = this.employeeForm.get('dateOfBirth');
    if (!dobControl || !dobControl.value) {
      console.warn('Date of Birth is empty');
      return;
    }
 
    // Format date to match expected format (YYYY-MM-DD)
    const userEnteredDob = formatDate(dobControl.value, 'yyyy-MM-dd', 'en-US');
   
   
    if (this.fullUserDetails && this.fullUserDetails.dateOfBirth) {
      // Format the API-returned DOB to ensure consistent comparison
      const apiDob = this.fullUserDetails.dateOfBirth.split('T')[0]; // Handle ISO date format if needed
      
     
      if (userEnteredDob === apiDob) {
        // DOB matches, show full details and populate edit form
        this.displayInfo = true;
        this.populateEditForm();
        
        this.error = null;
      } else {
        this.error = 'The date of birth does not match our records. Please try again or Kindly contact aiicontact@aiicoplc.com to resolve.';
        this.displayInfo = false;
      }
    } else {
      this.error = 'Unable to verify date of birth. Please try again or Kindly contact aiicontact@aiicoplc.com to resolve.';
      this.displayInfo = false;
    }
   
    this.cdr.detectChanges();
  }
 
  // Populate all fields in the edit form
  populateEditForm() {
    if (this.fullUserDetails) {
      // Populate all form fields
      this.editDetailsForm.patchValue({
        policyNumber: this.fullUserDetails.policyNumber || '',
        names: this.fullUserDetails.names || '',
        email: this.fullUserDetails.email || '',
        telephone: this.fullUserDetails.telephone || '',
        physicalAddress: this.fullUserDetails.physicalAddress || '',
        bvn: this.fullUserDetails.bvn || ''
      });
  
      // Disable fields that have values, including policy number
      const fieldsToCheck = ['policyNumber', 'names', 'email', 'telephone', 'physicalAddress', 'bvn'];
      fieldsToCheck.forEach(field => {
        const control = this.editDetailsForm.get(field);
        if (control) {
          if (this.fullUserDetails[field] && this.fullUserDetails[field].trim() !== '') {
            // If field has a value, disable the control
            control.disable();
          } else {
            // If field is empty, enable the control
            control.enable();
          }
        }
      });
  
      // Check if BVN exists
      this.hasBVN = !!(this.fullUserDetails.bvn && this.fullUserDetails.bvn.trim() !== '');
  
      
      this.cdr.detectChanges();
    } else {
      console.warn('fullUserDetails is null. Cannot populate edit form.');
    }
  }
 
  updateClientDetails() {
    // Check if terms have been accepted
    if (!this.termsAccepted) {
      this.openTermsModal();
      return;
    }
    
    // Enable all controls temporarily for validation and submission
    Object.keys(this.editDetailsForm.controls).forEach(key => {
      const control = this.editDetailsForm.get(key);
      if (control) {
        control.enable();
      }
    });
  
    if (this.editDetailsForm.valid && this.fullUserDetails) {
      this.updating = true;
  
      // Get updated values from the form
      const formValues = this.editDetailsForm.value;
  

      this.bVNService.UpdateClientDetails(
        formValues.policyNumber,
        formValues.email,
        formValues.telephone,
        formValues.physicalAddress,
        formValues.bvn
      ).subscribe({
        next: (response) => {
          
          
          if (response && (response.isSuccessful || response.result)) {
            // CHANGED: Show success modal instead of top alert message
            this.openSuccessModal();
            
            // Repopulate form and re-disable fields
            this.populateEditForm();
          } else {
            console.error('Unexpected response:', response);
            this.message.error(response.errorMessages || 'Failed to update details');
          }
          this.updating = false;
        },
        error: (err) => {
          console.error('Update error:', err);
          this.message.error('Error updating details');
          this.updating = false;
        }
      });
    } else {
      console.warn('Form is invalid or fullUserDetails is null. Cannot update.');
      this.editDetailsForm.markAllAsTouched();
    }
  }
 
  private handleError(message: string) {
    this.error = message;
    this.employeeInfo = null;
    this.fullUserDetails = null;
    this.policyVerified = false;
    this.displayInfo = false;
    this.hasBVN = false;
  }
}